import type { VehicleRentalLocation } from "@/generated/search.openapi";

const MAX_EXACT_DESK_DISTANCE_METERS = 150;
const EARTH_RADIUS_METERS = 6_371_000;

function containsNormalizedPhrase(text: string, phrase: string): boolean {
  const textTokens = text.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  const phraseTokens = phrase.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  if (phraseTokens.length === 0) return false;

  return textTokens.some((_, startIndex) =>
    phraseTokens.every(
      (token, offset) => textTokens[startIndex + offset] === token
    )
  );
}

function addressMatchesDescription(
  placeDescription: string,
  address?: string
): boolean {
  if (!address) return false;

  const descriptionTokens = new Set(
    placeDescription.toLowerCase().match(/[a-z0-9]+/g) ?? []
  );
  const addressTokens = address.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  const streetNumberIndex = addressTokens.findIndex((token) =>
    /^\d+$/.test(token)
  );
  if (streetNumberIndex < 0) return false;

  const branchTokens = addressTokens.slice(
    streetNumberIndex,
    streetNumberIndex + 2
  );
  return (
    branchTokens.length === 2 &&
    branchTokens.every((token) => descriptionTokens.has(token))
  );
}

function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

function distanceMeters(
  latitude: number,
  longitude: number,
  desk: VehicleRentalLocation
): number {
  const latitudeDelta = toRadians(desk.latitude - latitude);
  const longitudeDelta = toRadians(desk.longitude - longitude);
  const startLatitude = toRadians(latitude);
  const endLatitude = toRadians(desk.latitude);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(startLatitude) *
      Math.cos(endLatitude) *
      Math.sin(longitudeDelta / 2) ** 2;

  return (
    2 *
    EARTH_RADIUS_METERS *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
}

/**
 * Pin a Google rental-car place only when its coordinates identify one nearby
 * desk for the named vendor. Ambiguous or incomplete catalog data intentionally
 * falls back to the broad metro search.
 */
export function matchExactRentalDesk(
  placeDescription: string,
  latitude: number,
  longitude: number,
  desks: VehicleRentalLocation[]
): VehicleRentalLocation | null {
  const closeVendorDesks = desks.filter(
    (desk) =>
      containsNormalizedPhrase(placeDescription, desk.vendorName) &&
      addressMatchesDescription(placeDescription, desk.address) &&
      distanceMeters(latitude, longitude, desk) <=
        MAX_EXACT_DESK_DISTANCE_METERS
  );

  return closeVendorDesks.length === 1 ? closeVendorDesks[0] : null;
}
